import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Play, RefreshCw, ChevronRight, CheckCircle2, XCircle, AlertTriangle, Clock, Loader2, HelpCircle, ChevronDown } from 'lucide-react';
import { reconRulesApi } from '../../services/api';
import { formatDate } from '../../utils/formatters';
import { mockExecutions, mockProcesses, mockExecutionDetails } from './mockData';

const StatusBadge = ({ status }) => {
  const styles = {
    PASSED: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800',
    WARNING: 'bg-yellow-100 text-yellow-800',
    pass: 'bg-green-100 text-green-800',
    fail: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    skipped: 'bg-gray-100 text-gray-500',
  };
  const icons = {
    PASSED: <CheckCircle2 className="w-3 h-3" />,
    FAILED: <XCircle className="w-3 h-3" />,
    WARNING: <AlertTriangle className="w-3 h-3" />,
    pass: <CheckCircle2 className="w-3 h-3" />,
    fail: <XCircle className="w-3 h-3" />,
    warning: <AlertTriangle className="w-3 h-3" />,
    error: <XCircle className="w-3 h-3" />,
    skipped: <Clock className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {icons[status]} {status}
    </span>
  );
};

const HowTo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
          <HelpCircle className="w-4 h-4" /> How to use Execution Dashboard
        </span>
        <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
          <div>
            <p className="font-semibold mb-1">What is the Execution Dashboard?</p>
            <p>This dashboard lets you run reconciliation processes and view their results. Each run executes all rules attached to a process and logs pass/fail results per rule.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Steps</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li><strong>Quick Run</strong> - Use the buttons at the top to quickly execute any active process that has rulesets attached.</li>
              <li><strong>View Results</strong> - The execution history shows all runs with overall status (Passed/Failed/Warning).</li>
              <li><strong>Drill Down</strong> - Click on any run to expand and see per-rule results including expected vs actual values and execution time.</li>
              <li><strong>Filter</strong> - Filter by process or status to find specific runs. Use Refresh to get the latest results.</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold mb-1">Status Guide</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Passed</strong> - All rules passed validation</li>
              <li><strong>Failed</strong> - One or more required rules failed</li>
              <li><strong>Warning</strong> - No hard failures, but some rules returned warnings</li>
              <li><strong>Error</strong> - A rule encountered a SQL or execution error</li>
              <li><strong>Skipped</strong> - Rule was inactive and not executed</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ReconDashboard() {
  const [executions, setExecutions] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState(null);
  const [runDetails, setRunDetails] = useState({});
  const [filterProcess, setFilterProcess] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [executing, setExecuting] = useState(null);

  const loadData = useCallback(async () => {
    try {
      let eData, pData;
      try {
        const [execRes, procRes] = await Promise.all([
          reconRulesApi.listExecutions({ processId: filterProcess || undefined, status: filterStatus || undefined }),
          reconRulesApi.listProcesses(),
        ]);
        eData = execRes.data;
        pData = procRes.data;
      } catch { eData = []; pData = []; }
      setExecutions(eData && eData.length > 0 ? eData : mockExecutions);
      setProcesses(pData && pData.length > 0 ? pData : mockProcesses);
    } catch (err) {
      console.error('Failed to load executions:', err.message);
    } finally {
      setLoading(false);
    }
  }, [filterProcess, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleExpand = async (runId) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
      return;
    }
    setExpandedRun(runId);
    if (!runDetails[runId]) {
      try {
        const res = await reconRulesApi.getExecution(runId);
        const data = res.data;
        setRunDetails(prev => ({ ...prev, [runId]: data && data.length > 0 ? data : (mockExecutionDetails[runId] || []) }));
      } catch (err) {
        setRunDetails(prev => ({ ...prev, [runId]: mockExecutionDetails[runId] || [] }));
      }
    }
  };

  const handleExecute = async (processId) => {
    setExecuting(processId);
    try {
      await reconRulesApi.execute(processId);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert('Execution failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setExecuting(null);
    }
  };

  // KPI stats
  const totalRuns = executions.length;
  const passedRuns = executions.filter(e => e.overall_status === 'PASSED').length;
  const failedRuns = executions.filter(e => e.overall_status === 'FAILED').length;
  const passRate = totalRuns > 0 ? Math.round((passedRuns / totalRuns) * 100) : 0;

  const activeProcesses = processes.filter(p => p.is_active && p.ruleset_count > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-100 rounded-lg">
          <Activity className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Execution Dashboard</h1>
          <p className="text-sm text-gray-500">Run and monitor file reconciliation</p>
        </div>
      </div>

      <HowTo />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Runs</p>
          <p className="text-2xl font-bold text-gray-900">{totalRuns}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Pass Rate</p>
          <p className="text-2xl font-bold text-green-600">{passRate}%</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Passed</p>
          <p className="text-2xl font-bold text-green-600">{passedRuns}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Failed</p>
          <p className="text-2xl font-bold text-red-600">{failedRuns}</p>
        </div>
      </div>

      {/* Quick Run */}
      {activeProcesses.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Run</h3>
          <div className="flex flex-wrap gap-2">
            {activeProcesses.map(proc => (
              <button key={proc.process_id} onClick={() => handleExecute(proc.process_id)}
                disabled={executing === proc.process_id}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-amber-50 hover:border-amber-400 transition-colors disabled:opacity-50">
                {executing === proc.process_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                {proc.process_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <select value={filterProcess} onChange={e => { setFilterProcess(e.target.value); setLoading(true); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
          <option value="">All Processes</option>
          {processes.map(p => <option key={p.process_id} value={p.process_id}>{p.process_name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLoading(true); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400">
          <option value="">All Statuses</option>
          <option value="PASSED">Passed</option>
          <option value="FAILED">Failed</option>
          <option value="WARNING">Warning</option>
        </select>
        <button onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Execution History */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : executions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No execution runs found. Run a process to see results here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {executions.map(exec => (
            <div key={exec.run_id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div
                onClick={() => toggleExpand(exec.run_id)}
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expandedRun === exec.run_id ? 'rotate-90' : ''}`} />
                <div className="flex-1 flex items-center gap-4 flex-wrap">
                  <StatusBadge status={exec.overall_status} />
                  <span className="font-medium text-sm">{exec.process_name}</span>
                  <span className="text-xs text-gray-500">{exec.partner_name} / {exec.file_type}</span>
                  <span className="text-xs text-gray-400">{exec.direction}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="text-green-600">{exec.passed} passed</span>
                  {parseInt(exec.failed) > 0 && <span className="text-red-600">{exec.failed} failed</span>}
                  {parseInt(exec.warnings) > 0 && <span className="text-yellow-600">{exec.warnings} warnings</span>}
                  {parseInt(exec.errors) > 0 && <span className="text-red-600">{exec.errors} errors</span>}
                  <span>{exec.total_rules} rules</span>
                  <span>{exec.executed_by}</span>
                  <span>{formatDate(exec.started_at)}</span>
                </div>
              </div>

              {/* Expanded Detail */}
              {expandedRun === exec.run_id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  {!runDetails[exec.run_id] ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 uppercase">
                            <th className="px-3 py-2">Rule</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Ruleset</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Expected</th>
                            <th className="px-3 py-2">Actual</th>
                            <th className="px-3 py-2">Time (ms)</th>
                            <th className="px-3 py-2">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {runDetails[exec.run_id].map((detail, idx) => (
                            <tr key={idx} className={`border-t border-gray-200 ${['fail', 'error'].includes(detail.status) ? 'bg-red-50' : detail.status === 'warning' ? 'bg-yellow-50' : ''}`}>
                              <td className="px-3 py-2 font-medium">{detail.rule_name}</td>
                              <td className="px-3 py-2 text-gray-500">{detail.rule_type}</td>
                              <td className="px-3 py-2 text-gray-500">{detail.ruleset_name}</td>
                              <td className="px-3 py-2"><StatusBadge status={detail.status} /></td>
                              <td className="px-3 py-2 text-gray-600">{detail.expected_value || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{detail.actual_value || '-'}</td>
                              <td className="px-3 py-2 text-gray-500">{detail.execution_ms || '-'}</td>
                              <td className="px-3 py-2 text-red-600 text-xs max-w-xs truncate">{detail.error_message || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
